import React, { useEffect, useRef, useState, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import FormApi from "../../../Api/FormApi"
import {
  Button,
  Col,
  Row,
  Input,
  Card,
  CardBody,
  Badge,
  InputGroup,
  InputGroupText,
} from "reactstrap"
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb"
import DataTableLoader from "SkeletonLoader/DataTableLoader"
import { Toast } from "primereact/toast"
import { ActionIcon } from "components/Common"

const ResponseList = () => {
  document.title = "Submitted Forms"
  const navigate = useNavigate()
  const toast = useRef(null)
  const accessToken = localStorage.getItem("accessToken")

  const [responses, setResponses] = useState([])
  const [filteredResponses, setFilteredResponses] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")

  const breadcrumbItems = [
    { label: "Pixwik", link: "/" },
    { label: "Forms", link: "/forms" },
    { label: "Submitted Forms" },
  ]

  const handleBreadcrumbClick = link => {
    if (link !== "#") {
      navigate(link)
    }
  }

  const getAllResponses = useCallback(() => {
    setLoading(true)
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }

    FormApi.getAllResponses(config)
      .then(response => {
        setLoading(false)
        if (response.data.status) {
          setResponses(response.data.responses)
          setFilteredResponses(response.data.responses)
        }
      })
      .catch(error => {
        console.error("Error fetching responses:", error)
        setLoading(false)
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error.response?.data?.message || "Failed to fetch responses"
        })
      })
  }, [accessToken])

  useEffect(() => {
    getAllResponses()
  }, [getAllResponses])

  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue)
    if (!searchValue.trim()) {
      setFilteredResponses(responses)
    } else {
      const filtered = responses.filter(resp =>
        (resp.respondentName?.toLowerCase() || "").includes(searchValue.toLowerCase()) ||
        (resp.respondentEmail?.toLowerCase() || "").includes(searchValue.toLowerCase()) ||
        (resp.formId?.formName?.toLowerCase() || "").includes(searchValue.toLowerCase()) ||
        (resp.status?.toLowerCase() || "").includes(searchValue.toLowerCase())
      )
      setFilteredResponses(filtered)
    }
  }

  const handleViewResponse = (responseId) => {
    navigate(`/view-response/${responseId}`)
  }

  return (
    <>
      <Toast ref={toast} />
      <div className="page-content">
        <div className="container-fluid">
          <CustomBreadCrumb
            data={breadcrumbItems}
            pageTitle="Submitted Forms"
            handleClick={handleBreadcrumbClick}
          />

          <Row className="mb-4 mt-2">
            <Col xl={12}>
              <Card>
                <CardBody>
                  <Row className="g-3">
                    <Col md={4}>
                      <InputGroup>
                        <InputGroupText>
                          <i className="mdi mdi-magnify"></i>
                        </InputGroupText>
                        <Input
                          type="text"
                          placeholder="Search by respondent, form or status..."
                          value={searchTerm}
                          onChange={(e) => handleSearch(e.target.value)}
                        />
                      </InputGroup>
                    </Col>
                    <Col md={8} className="text-end">
                       <Button color="light" onClick={getAllResponses} disabled={loading}>
                         <i className="mdi mdi-refresh me-1"></i> Refresh
                       </Button>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  {loading ? (
                    <DataTableLoader cols={6} />
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover align-middle table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Respondent</th>
                            <th>Email</th>
                            <th>Form</th>
                            <th>Status</th>
                            <th>Submitted At</th>
                            <th className="text-center">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredResponses.length > 0 ? (
                            filteredResponses.map((item, index) => (
                              <tr key={item._id}>
                                <td>{item.respondentName || "Anonymous"}</td>
                                <td>{item.respondentEmail || "N/A"}</td>
                                <td>
                                  <span className="fw-medium">
                                    {item.formId?.formName || "Unknown Form"}
                                  </span>
                                </td>
                                <td>
                                  <Badge 
                                    color={
                                      item.status === "submitted" ? "success" : 
                                      item.status === "spam" ? "danger" : "secondary"
                                    }
                                  >
                                    {item.status}
                                  </Badge>
                                </td>
                                <td>{new Date(item.createdAt).toLocaleString()}</td>
                                <td className="text-center">
                                  <ActionIcon
                                    type="view"
                                    onClick={() => handleViewResponse(item._id)}
                                    tooltip="View Details"
                                  />
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="6" className="text-center py-4">
                                No responses found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </>
  )
}

export default ResponseList
